import React, { useEffect, useState } from 'react';
import {
  RefreshCw, Award, Download, Ban, ShieldCheck, ExternalLink, Plus,
  AlertTriangle,
} from 'lucide-react';
import { Button, Pill, Card, EmptyState, Slideout, Modal } from '../../index';
import { Pagination, usePaginationState } from '../../../common/Pagination';
import { StatusBadge } from '../widgets/StatusBadge';
import {
  getComplianceCertificates, generateComplianceCertificate,
  revokeComplianceCertificate, verifyComplianceCertificate,
} from '../../../../services/api';

/**
 * CertificatesSlideout — V2 Certificates workspace (Phase B4 / G317).
 *
 * Replaces ?legacy=1&tab=CERTIFICATES. Production rebuild with:
 *   - Filter by framework (SOC 2 / HIPAA / PCI / ISO 27001 / all)
 *   - Table: cert ID | framework | status | issued | expiry | actions
 *   - Inline actions: Download (PDF/JSON), Revoke (w/ reason), Verify
 *     integrity (hash-chain check)
 *   - Generate-from-workflow modal: framework picker + workflow run ID
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.addToast
 */
const FRAMEWORK_OPTIONS = ['', 'SOC2', 'HIPAA', 'PCI', 'ISO27001'];

export function CertificatesSlideout({ isOpen, onClose, addToast }) {
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [revokeModal, setRevokeModal] = useState(null);
  const [generateModal, setGenerateModal] = useState(false);
  const pg = usePaginationState(20);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await getComplianceCertificates(filterType || undefined);
      const list = Array.isArray(r) ? r : (r?.data?.content || r?.data?.items || r?.data || r?.items || []);
      setItems(Array.isArray(list) ? list : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isOpen) reload(); /* eslint-disable-next-line */ }, [isOpen, filterType]);

  const visible = pg.paginate(items);

  const handleDownload = async (cert) => {
    setBusy(cert.id);
    try {
      /* Direct browser navigation: server streams the binary with a
       * Content-Disposition header. No JSON wrapping. */
      window.open(`/api/v1/compliance/certificates/${cert.id}/download`, '_blank');
      addToast?.(`Downloading ${cert.frameworkType || 'certificate'}.`, 'success');
    } catch (e) {
      addToast?.(`Download failed: ${e?.message || e}`, 'error');
    } finally { setBusy(null); }
  };

  const handleVerify = async (cert) => {
    setBusy(cert.id);
    try {
      const r = await verifyComplianceCertificate(cert.id);
      const valid = r?.data?.valid ?? r?.valid;
      if (r?.ok === false) addToast?.(`Verify failed: ${r.error}`, 'error');
      else if (valid === false) addToast?.(`⚠ Certificate hash-chain BROKEN — investigate.`, 'error');
      else addToast?.(`Certificate verified — hash chain intact.`, 'success');
    } finally { setBusy(null); }
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={840}
      ariaLabel="Certificates workspace"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Award size={18} className="text-v2-action" />
            <h2 className="text-[16px] font-bold text-v2-ink m-0">Compliance certificates</h2>
            <Pill tone="cyan" size="sm">{items.length}</Pill>
          </div>
          <p className="text-[12.5px] text-v2-ink-secondary m-0 mb-3">
            Generated evidence artefacts (SOC 2, HIPAA, PCI, ISO 27001) — downloadable
            evidence packages with a sealed hash chain.
          </p>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted">Framework:</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                    className="border border-v2-border-subtle rounded-v2-sm px-2 py-1 bg-v2-surface text-[12.5px]">
              {FRAMEWORK_OPTIONS.map((f) => (
                <option key={f} value={f}>{f || 'All frameworks'}</option>
              ))}
            </select>
            <Button variant="primary" size="sm" iconLeft={<Plus size={13} />}
                    className="ml-auto" onClick={() => setGenerateModal(true)}>
              Generate certificate
            </Button>
            <Button variant="ghost" size="sm"
                    iconLeft={<RefreshCw size={12} className={loading ? 'animate-spin' : ''} />}
                    onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>
      }
    >
      {loading && items.length === 0 && (
        <p className="text-[12.5px] text-v2-ink-muted">Loading certificates…</p>
      )}
      {!loading && items.length === 0 && (
        <EmptyState
          icon={<Award size={20} />}
          title="No certificates yet"
          description="Generate a SOC 2 / HIPAA / PCI / ISO 27001 certificate from a completed compliance workflow run."
          primaryAction={
            <Button variant="primary" iconLeft={<Plus size={14} />} onClick={() => setGenerateModal(true)}>
              Generate certificate
            </Button>
          }
        />
      )}
      {items.length > 0 && (
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-v2-border-subtle">
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Certificate</th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Framework</th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Status</th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Issued</th>
              <th className="text-left text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Expires</th>
              <th className="text-right text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => {
              const status = (c.status || (c.revoked ? 'REVOKED'
                            : c.expiryDate && new Date(c.expiryDate) < new Date() ? 'EXPIRED'
                            : 'ACTIVE')).toUpperCase();
              const expiresSoon = c.expiryDate && status === 'ACTIVE'
                                && new Date(c.expiryDate).getTime() - Date.now() < 30 * 86_400_000;
              return (
                <tr key={c.id} className="border-b border-v2-border-subtle/50 hover:bg-v2-muted/40">
                  <td className="py-2">
                    <div className="font-mono-v2 text-[11px] text-v2-ink">{String(c.id).slice(0, 16)}…</div>
                    {c.workflowRunId && (
                      <div className="text-[10.5px] text-v2-ink-muted">
                        run {String(c.workflowRunId).slice(0, 12)}…
                      </div>
                    )}
                  </td>
                  <td className="py-2">
                    <Pill tone="cyan" size="sm">{c.frameworkType || c.type || '—'}</Pill>
                  </td>
                  <td className="py-2"><StatusBadge status={status} /></td>
                  <td className="py-2 font-mono-v2 text-[11px] text-v2-ink-muted">
                    {c.issuedDate ? new Date(c.issuedDate).toLocaleDateString() : c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="py-2 font-mono-v2 text-[11px]">
                    {c.expiryDate ? (
                      <span className={expiresSoon ? 'text-v2-warning font-semibold' : 'text-v2-ink-muted'}>
                        {new Date(c.expiryDate).toLocaleDateString()}
                        {expiresSoon && <AlertTriangle size={10} className="inline ml-1 -mt-0.5" />}
                      </span>
                    ) : <span className="text-v2-ink-muted">—</span>}
                  </td>
                  <td className="py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button variant="ghost" size="sm" iconLeft={<Download size={11} />}
                              disabled={busy === c.id || status === 'REVOKED'}
                              onClick={() => handleDownload(c)}>
                        Download
                      </Button>
                      <Button variant="ghost" size="sm" iconLeft={<ShieldCheck size={11} />}
                              disabled={busy === c.id}
                              onClick={() => handleVerify(c)}>
                        Verify
                      </Button>
                      {status === 'ACTIVE' && (
                        <Button variant="ghost" size="sm" iconLeft={<Ban size={11} />}
                                disabled={busy === c.id}
                                onClick={() => setRevokeModal(c)}>
                          Revoke
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {items.length > 0 && (
        <div className="mt-3">
          <Pagination
            totalItems={items.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="certificates"
          />
        </div>
      )}

      {revokeModal && (
        <RevokeCertificateModal
          cert={revokeModal}
          onClose={() => setRevokeModal(null)}
          onSubmit={async (reason) => {
            const r = await revokeComplianceCertificate(revokeModal.id, reason);
            if (r?.ok === false) throw new Error(r.error || 'Revoke failed');
            addToast?.('Certificate revoked.', 'warn');
            reload();
          }}
        />
      )}
      {generateModal && (
        <GenerateCertificateModal
          onClose={() => setGenerateModal(false)}
          onSubmit={async ({ workflowRunId, frameworkType }) => {
            const r = await generateComplianceCertificate(workflowRunId, frameworkType);
            if (r?.ok === false) throw new Error(r.error || 'Generation failed');
            addToast?.(`Certificate generated.`, 'success');
            reload();
          }}
        />
      )}
    </Slideout>
  );
}

function RevokeCertificateModal({ cert, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  return (
    <Modal isOpen onClose={submitting ? () => {} : onClose} size="md"
           title={<span className="inline-flex items-center gap-2"><Ban size={18} className="text-v2-danger" /> Revoke certificate</span>}
           footer={<>
             <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
             <Button variant="danger" disabled={submitting || !reason.trim()}
                     onClick={async () => {
                       setSubmitting(true); setError(null);
                       try { await onSubmit(reason.trim()); onClose(); }
                       catch (e) { setError(e?.message || 'Revocation failed'); }
                       finally { setSubmitting(false); }
                     }}>
               {submitting ? 'Revoking…' : 'Revoke'}
             </Button>
           </>}>
      <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm p-3 mb-3 text-[12.5px] text-v2-danger">
        <strong>Revocation is permanent.</strong> The certificate will be marked REVOKED in
        the audit log; downloads will fail. Use this only when the underlying evidence is
        no longer valid (control failure, scope change, retracted attestation).
      </div>
      <div className="bg-v2-muted rounded-v2-sm p-2.5 mb-3 text-[12px]">
        <div className="font-semibold text-v2-ink">{cert.frameworkType || 'Certificate'}</div>
        <div className="text-v2-ink-muted font-mono-v2 text-[11px]">
          ID {String(cert.id).slice(0, 16)}…
        </div>
      </div>
      <label className="block">
        <span className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
          Reason <span className="text-v2-danger">*</span>
        </span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Underlying SOC 2 control C-04 failed re-audit; certificate no longer represents valid evidence."
          rows={3}
          className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none resize-y"
          disabled={submitting}
        />
      </label>
      {error && (
        <div className="mt-3 bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-danger">
          {error}
        </div>
      )}
    </Modal>
  );
}

function GenerateCertificateModal({ onClose, onSubmit }) {
  const [frameworkType, setFrameworkType] = useState('SOC2');
  const [workflowRunId, setWorkflowRunId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  return (
    /* Phase 5B — Generate compliance certificate: Modal → Slideout per G378. */
    <Slideout isOpen onClose={submitting ? undefined : onClose} width={560}
              ariaLabel="Generate compliance certificate"
              header={<h2 className="text-[15px] font-bold m-0 inline-flex items-center gap-2"><Award size={16} className="text-v2-action" /> Generate compliance certificate</h2>}
              footer={
                <div className="flex items-center justify-end gap-2 w-full">
                  <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
                  <Button variant="primary" disabled={submitting || !workflowRunId.trim()}
                          onClick={async () => {
                            setSubmitting(true); setError(null);
                            try { await onSubmit({ workflowRunId: workflowRunId.trim(), frameworkType }); onClose(); }
                            catch (e) { setError(e?.message || 'Generation failed'); }
                            finally { setSubmitting(false); }
                          }}>
                    {submitting ? 'Generating…' : 'Generate'}
                  </Button>
                </div>
              }>
      <p className="text-[12.5px] text-v2-ink mb-4">
        Generates a sealed evidence certificate from a completed compliance workflow run.
        The workflow run must have produced the required artefacts for the chosen framework.
      </p>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">Framework</span>
          <select value={frameworkType} onChange={(e) => setFrameworkType(e.target.value)}
                  className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] bg-v2-surface">
            <option value="SOC2">SOC 2 Type II</option>
            <option value="HIPAA">HIPAA</option>
            <option value="PCI">PCI DSS</option>
            <option value="ISO27001">ISO 27001</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
            Workflow run ID <span className="text-v2-danger">*</span>
          </span>
          <input
            type="text"
            value={workflowRunId}
            onChange={(e) => setWorkflowRunId(e.target.value)}
            placeholder="UUID of the completed compliance workflow run"
            className="w-full border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] font-mono-v2 bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none"
            disabled={submitting}
          />
          <span className="text-[10.5px] text-v2-ink-muted mt-1 block">
            Find this on the Workflows page → run history → completed run.
          </span>
        </label>
      </div>
      {error && (
        <div className="mt-3 bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm px-3 py-2 text-[12px] text-v2-danger">
          {error}
        </div>
      )}
    </Slideout>
  );
}

export default CertificatesSlideout;
